import { Box, SegmentedControl } from "@mantine/core";
import { IconQrcode } from "@tabler/icons-react";
import { FIELD_LABELS } from "./badgeConstants";
import ResizeHandle from "../ResizeHandle";

const renderElementContent = (el) => {
  if (el.type === "qr") {
    return (
      <IconQrcode
        size="80%"
        style={{ width: "100%", height: "100%" }}
        stroke={1}
        color="#000000"
      />
    );
  }
  if (el.type === "rectangle") return null;
  if (el.type === "field") return `{{${FIELD_LABELS[el.fieldKey]}}}`;
  return el.text || "";
};

// Preview surface: layer switcher, scaled card pane, and the draggable /
// resizable elements. All interaction is driven by the shared canvas editor.
export default function BadgeCanvas({
  containerRef,
  paneRef,
  scale,
  nativeWidth,
  nativeHeight,
  bgImage,
  backgroundFit = "fit",
  elements,
  selectedId,
  setSelectedId,
  previewLayer,
  setPreviewLayer,
  draggingId,
  onElementPointerDown,
  onResizePointerDown,
  onPointerMove,
  onPointerUp,
}) {
  const showBackground = previewLayer !== "print";
  const showElements = previewLayer !== "stock";

  const backgroundSize =
    backgroundFit === "fill"
      ? "cover"
      : backgroundFit === "stretch"
        ? "100% 100%"
        : "contain";

  return (
    <Box
      ref={containerRef}
      onPointerDown={(e) => {
        if (e.target === containerRef.current || e.target === paneRef.current)
          setSelectedId(null);
      }}
      style={{
        flex: 1,
        position: "relative",
        backgroundColor: "#e9ecef",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <Box
        style={{ position: "absolute", top: 16, zIndex: 5 }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <SegmentedControl
          size="xs"
          value={previewLayer}
          onChange={setPreviewLayer}
          data={[
            { label: "Print", value: "print" },
            { label: "Stock", value: "stock" },
            { label: "Preview", value: "preview" },
          ]}
        />
      </Box>

      <Box
        ref={paneRef}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          width: `${nativeWidth}px`,
          height: `${nativeHeight}px`,
          backgroundColor: "white",
          backgroundImage:
            showBackground && bgImage ? `url(${bgImage})` : "none",
          backgroundSize,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          position: "relative",
        }}
      >
        {showElements &&
          elements.map((el) => {
            const isSelected = selectedId === el.id;
            const isText = el.type === "text" || el.type === "field";
            return (
              <div
                key={el.id}
                onPointerDown={(e) => onElementPointerDown(e, el)}
                style={{
                  position: "absolute",
                  left: `${el.xPct}%`,
                  top: `${el.yPct}%`,
                  width: `${el.widthPct}%`,
                  height: `${el.heightPct}%`,
                  backgroundColor:
                    el.type === "rectangle"
                      ? el.fill
                      : isSelected
                        ? "rgba(51, 154, 240, 0.1)"
                        : "transparent",
                  border: isSelected
                    ? "2px dashed #339af0"
                    : "1px dashed transparent",
                  fontFamily: "Montserrat, sans-serif",
                  color: isText ? el.color : undefined,
                  fontSize: isText ? `${el.fontSize}px` : undefined,
                  textAlign: isText ? el.align : undefined,
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    el.align === "center"
                      ? "center"
                      : el.align === "right"
                        ? "flex-end"
                        : "flex-start",
                  cursor: draggingId.current === el.id ? "grabbing" : "grab",
                  userSelect: "none",
                  touchAction: "none",
                  zIndex: isSelected ? 10 : 1,
                  overflow: "hidden",
                  padding: isText ? "4px" : 0,
                }}
              >
                {renderElementContent(el)}

                {isSelected && (
                  <ResizeHandle
                    onPointerDown={(e) =>
                      onResizePointerDown(e, el.id, el.widthPct, el.heightPct)
                    }
                  />
                )}
              </div>
            );
          })}
      </Box>
    </Box>
  );
}
